import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/datetime";
import api, { formatApiError } from "@/lib/api";
import { pickLocale } from "@/lib/locale";
import {
  Plus,
  CaretRight,
  CaretDown,
  CheckSquare,
  MinusSquare,
  Square,
  EnvelopeSimple,
  Trash,
  Pencil,
  PaperPlaneTilt,
  Eye,
  CircleNotch,
  CheckCircle,
  X as XIcon,
  ChatCircleDots,
  ListChecks,
  Copy,
  Check,
  Envelope,
  DeviceMobile,
  Key,
  FloppyDisk,
  Play,
  Warning,
} from "@phosphor-icons/react";

// -----------------------------------------------------------------------------
// Static mappings
// -----------------------------------------------------------------------------
const TRIGGERS = [
  { key: "on_booking_confirmed",     label: "Pri potvrđenoj rezervaciji", needsOffset: false, needsTime: false, hint: "Šalje se odmah po primitku nove rezervacije" },
  { key: "n_days_before_checkin",    label: "N dana prije dolaska",       needsOffset: true,  needsTime: true,  hint: "Preporuka: 3 dana prije za slanje check-in linka" },
  { key: "on_checkin_day",           label: "Na dan dolaska",             needsOffset: false, needsTime: true,  hint: "Šalje se ujutro na dan dolaska (WiFi, ulazni kod...)" },
  { key: "on_checkin_form_submitted",label: "Kada gost ispuni check-in",  needsOffset: false, needsTime: false, hint: "Zahvala + potvrda podataka" },
  { key: "n_days_after_checkout",    label: "N dana nakon odjave",        needsOffset: true,  needsTime: true,  hint: "Idealno za molbu za recenziju" },
];
const TRIGGER_MAP = Object.fromEntries(TRIGGERS.map((t) => [t.key, t]));

const CHANNELS = ["BookingCom", "AirBNB", "Expedia"];
const CHANNEL_LABEL = { BookingCom: "Booking.com", AirBNB: "Airbnb", Expedia: "Expedia" };

const LANG_TABS = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "hr", label: "Hrvatski", flag: "🇭🇷" },
  { code: "de", label: "Deutsch",  flag: "🇩🇪" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
];

const VARIABLES = [
  "guest_first_name", "guest_last_name", "guest_full_name", "guest_email", "guest_phone",
  "reservation_code", "check_in_date", "check_out_date", "nights", "adults", "children",
  "property_name", "property_address", "property_city", "unit_name", "unit_code",
  "checkin_link", "check_in_time", "check_out_time",
  "unit_instructions_link_en", "unit_instructions_link_hr", "unit_instructions_link_de", "unit_instructions_link_it",
  "wifi_code", "access_code", "tenant_name",
];

const emptyTemplate = () => ({
  name: "",
  description: "",
  trigger_type: "n_days_before_checkin",
  trigger_offset_days: 3,
  trigger_time: "10:00",
  channels: [],
  property_ids: [],
  unit_ids: [],
  delivery_channel: "channex",
  // F8.5: multi-channel delivery (user can pick Email + OTA + SMS together).
  // Backward compat: if a loaded template only has delivery_channel, we seed
  // this from it on edit (see `edit(t)` handler below).
  delivery_channels: ["channex"],
  email_subject: "",
  email_subjects: { en: "" },
  bodies: { en: "" },
  active: true,
  send_once_per_reservation: true,
});

const DELIVERY_CHANNELS = [
  { key: "channex", label: "OTA Inbox (Booking.com / Airbnb / Expedia)", hint: "Šalje poruku kroz OTA chat — zahtijeva povezanu rezervaciju." },
  { key: "email",   label: "Email (Resend)",                             hint: "Šalje email na primary_guest_email — zahtijeva Resend ključ u Postavkama." },
  { key: "sms",     label: "SMS (Twilio)",                               hint: "Šalje SMS na primary_guest_phone — zahtijeva Twilio ključeve u Postavkama." },
];

// -----------------------------------------------------------------------------
// Main page
// -----------------------------------------------------------------------------
export default function MessageTemplatesPage() {
  const [tab, setTab] = useState("list"); // list | log
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/message-templates")
      .then((r) => setTemplates(r.data.items || []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
    api.get("/message-templates/delivery-log?limit=200")
      .then((r) => setLogs(r.data.items || []))
      .catch((e) => { setLogs([]); toast.error(`Log slanja: ${formatApiError(e)}`); });
  };
  useEffect(load, []);

  const openNew = () => { setEditing(emptyTemplate()); setEditorOpen(true); };
  const openEdit = (t) => {
    // Backward compat: legacy templates have only `delivery_channel` (str).
    // Seed `delivery_channels` (array) from it so the multi-select renders.
    const seeded = {
      ...t,
      delivery_channels: (Array.isArray(t.delivery_channels) && t.delivery_channels.length
        ? t.delivery_channels
        : [t.delivery_channel || "channex"]),
      // Legacy single subject becomes the EN subject
      email_subjects: (t.email_subjects && Object.keys(t.email_subjects).length)
        ? t.email_subjects
        : { en: t.email_subject || "" },
    };
    setEditing(seeded);
    setEditorOpen(true);
  };

  const onDelete = async (t) => {
    if (!window.confirm(`Obriši template "${t.name}"?`)) return;
    await api.delete(`/message-templates/${t.id}`);
    load();
  };

  const onToggleActive = async (t) => {
    await api.put(`/message-templates/${t.id}`, { active: !t.active });
    load();
  };

  return (
    <div className="rw-page" data-testid="templates-page">
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="hidden font-heading text-2xl font-bold tracking-tight text-foreground sm:block">Automatske poruke</h1>
          <p className="hidden text-sm text-muted-foreground sm:mt-1 sm:block">
            Automatske poruke za goste — šalju se kroz OTA inbox, email (Resend) ili SMS (Twilio).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RunSchedulerNowButton onDone={load} />
          <button onClick={openNew} className="inline-flex h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90" data-testid="templates-new-btn">
            <Plus size={16} weight="bold" /> Novi predložak
          </button>
        </div>
      </div>

      <div className="flex border-b border-border sm:gap-1">
        {[
          { k: "list",      label: "Predlošci",  icon: ListChecks,      testid: "tab-list" },
          { k: "log",       label: "Log slanja", icon: PaperPlaneTilt,  testid: "tab-log" },
          { k: "providers", label: "Provideri",  icon: Key,             testid: "tab-providers" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={t.testid}
            className={`flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-2.5 text-sm font-medium transition sm:flex-none sm:px-3 ${
              tab === t.k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "list" ? (
        <TemplateList
          templates={templates}
          loading={loading}
          onEdit={openEdit}
          onDelete={onDelete}
          onToggleActive={onToggleActive}
          onNew={openNew}
        />
      ) : tab === "log" ? (
        <DeliveryLog logs={logs} templates={templates} />
      ) : (
        <ProvidersSettings />
      )}

      {editorOpen && (
        <EditorModal
          initial={editing}
          onClose={() => setEditorOpen(false)}
          onSaved={() => { setEditorOpen(false); load(); }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Template list
// -----------------------------------------------------------------------------
function TemplateList({ templates, loading, onEdit, onDelete, onToggleActive, onNew }) {
  if (loading) {
    return <div className="flex h-32 items-center justify-center"><CircleNotch size={20} className="animate-spin text-muted-foreground" /></div>;
  }
  if (templates.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-10 text-center" data-testid="templates-empty">
        <ChatCircleDots size={40} className="mx-auto mb-3 text-muted-foreground/60" />
        <p className="text-sm font-medium text-foreground">Još nema template-ova</p>
        <p className="mt-1 text-xs text-muted-foreground">Kreirajte prvi template za automatsko slanje check-in linka.</p>
        <button onClick={onNew} className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:opacity-90">
          <Plus size={14} /> Novi template
        </button>
      </div>
    );
  }
  const chanLabel = { email: "Email", sms: "SMS", channex: "OTA" };
  return (
    <>
    <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card md:hidden" data-testid="templates-cards">
      {templates.map((t) => {
        const trig = TRIGGER_MAP[t.trigger_type] || {};
        const chans = (Array.isArray(t.delivery_channels) && t.delivery_channels.length) ? t.delivery_channels : [t.delivery_channel || "channex"];
        return (
          <div key={t.id} className="flex items-center gap-3 px-3 py-3" data-testid={`template-card-${t.id}`}>
            <button onClick={() => onEdit(t)} className="min-w-0 flex-1 text-left">
              <p className="truncate text-sm font-semibold text-foreground">{t.name}</p>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                {trig.label || t.trigger_type}{trig.needsOffset ? ` · ${t.trigger_offset_days} d` : ""} · {chans.map((c) => chanLabel[c] || c).join(", ")}
              </p>
            </button>
            <button
              onClick={() => onToggleActive(t)}
              className={`relative inline-block h-5 w-9 shrink-0 rounded-full transition ${t.active ? "bg-emerald-500" : "bg-muted-foreground/30"}`}
              aria-label={t.active ? "Aktivan" : "Neaktivan"}
              data-testid={`template-card-toggle-${t.id}`}
            >
              <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${t.active ? "left-4" : "left-0.5"}`} />
            </button>
            <button onClick={() => onDelete(t)} className="shrink-0 rounded p-1.5 text-muted-foreground hover:text-rose-600" aria-label="Obriši" data-testid={`template-card-delete-${t.id}`}>
              <Trash size={15} />
            </button>
          </div>
        );
      })}
    </div>
    <div className="hidden overflow-x-auto rounded-lg border border-border md:block">
      <table className="w-full min-w-[600px] text-sm">
        <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-2.5">Naziv</th>
            <th className="px-4 py-2.5">Trigger</th>
            <th className="px-4 py-2.5">Dostava</th>
            <th className="px-4 py-2.5">Kanali</th>
            <th className="px-4 py-2.5">Jezici</th>
            <th className="px-4 py-2.5">Aktivan</th>
            <th className="px-4 py-2.5"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {templates.map((t) => {
            const trig = TRIGGER_MAP[t.trigger_type] || {};
            const langs = Object.keys(t.bodies || {}).filter((k) => (t.bodies[k] || "").trim());
            return (
              <tr key={t.id} className="hover:bg-muted/30" data-testid={`template-row-${t.id}`}>
                <td className="px-4 py-3">
                  <p className="font-medium text-foreground">{t.name}</p>
                  {t.description && <p className="text-xs text-muted-foreground">{t.description}</p>}
                </td>
                <td className="px-4 py-3 text-xs">
                  <p>{trig.label || t.trigger_type}</p>
                  <p className="text-muted-foreground">
                    {trig.needsOffset ? `${t.trigger_offset_days} dana` : ""}
                    {trig.needsTime ? ` · ${t.trigger_time}` : ""}
                  </p>
                </td>
                <td className="px-4 py-3 text-xs">
                  {/* F8.5: show ALL configured delivery channels — fall back to
                      legacy single-channel field when array is empty. */}
                  <div className="flex flex-wrap gap-1">
                    {(() => {
                      const chans = (Array.isArray(t.delivery_channels) && t.delivery_channels.length)
                        ? t.delivery_channels
                        : [t.delivery_channel || "channex"];
                      const label = {
                        email: "📧 Email",
                        sms: "📱 SMS",
                        channex: "💬 OTA",
                      };
                      return chans.map((c) => (
                        <span key={c} className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-[11px] font-medium">
                          {label[c] || c}
                        </span>
                      ));
                    })()}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs">
                  {t.channels?.length ? `OTA inbox: ${t.channels.map((c) => CHANNEL_LABEL[c] || c).join(", ")}` : <span className="text-muted-foreground">Svi</span>}
                </td>
                <td className="px-4 py-3 text-xs">
                  {langs.map((l) => LANG_TABS.find((lt) => lt.code === l)?.flag || l).join(" ") || <span className="text-muted-foreground">Nedefinirano</span>}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => onToggleActive(t)}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      t.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"
                    }`}
                    data-testid={`template-toggle-${t.id}`}
                  >
                    {t.active ? <><CheckCircle size={10} weight="fill" /> Aktivan</> : "Neaktivan"}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => onEdit(t)} className="mr-1 rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" title="Uredi" data-testid={`template-edit-${t.id}`}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => onDelete(t)} className="rounded p-1.5 text-muted-foreground hover:bg-rose-50 hover:text-rose-600" title="Obriši" data-testid={`template-delete-${t.id}`}>
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}

// -----------------------------------------------------------------------------
// Delivery log
// -----------------------------------------------------------------------------
function DeliveryLog({ logs, templates }) {
  const tplMap = useMemo(() => Object.fromEntries(templates.map((t) => [t.id, t])), [templates]);
  const [detailLog, setDetailLog] = useState(null);
  if (logs.length === 0) {
    return <p className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="log-empty">Nema slanja u zadnjih 200 zapisa.</p>;
  }
  return (
    <>
      <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card md:hidden" data-testid="delivery-log-cards">
        {logs.map((l) => {
          const tone = l.status === "sent" ? "bg-emerald-500" : l.status === "failed" ? "bg-rose-500" : "bg-slate-400";
          return (
            <button key={l.id} type="button" onClick={() => setDetailLog(l)} className="flex w-full items-center gap-3 px-3 py-3 text-left" data-testid={`log-card-${l.id}`}>
              <span className={`h-2 w-2 shrink-0 rounded-full ${tone}`} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{l.template_name || tplMap[l.template_id]?.name || l.template_id}</p>
                <p className="truncate text-xs text-muted-foreground">{fmtDateTime(l.sent_at)} · {CHANNEL_LABEL[l.provider] || l.provider || "—"} · {(l.language_used || "").toUpperCase()}</p>
              </div>
              <CaretRight size={14} className="shrink-0 text-muted-foreground" />
            </button>
          );
        })}
      </div>
      <div className="hidden overflow-x-auto rounded-lg border border-border md:block" data-testid="delivery-log-table">
        <table className="w-full min-w-[600px] whitespace-nowrap text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5">Vrijeme</th>
              <th className="px-4 py-2.5">Template</th>
              <th className="px-4 py-2.5">Rezervacija</th>
              <th className="px-4 py-2.5">Kanal</th>
              <th className="px-4 py-2.5">Jezik</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {logs.map((l) => {
              // Every row is clickable so user can inspect WHERE the message
              // went and what it said — not just errors. For sent-email-to-
              // Booking.com proxy we also flag it in amber so user sees that
              // delivery is likely silent-drop.
              const isProxySent = l.status === "sent"
                && l.provider === "email"
                && (l.recipient || "").toLowerCase().endsWith(".guest.booking.com");
              return (
                <tr
                  key={l.id}
                  data-testid={`log-row-${l.id}`}
                  onClick={() => setDetailLog(l)}
                  className={`cursor-pointer transition ${l.status === "failed" ? "hover:bg-rose-50/60" : isProxySent ? "hover:bg-amber-50/60" : "hover:bg-muted/40"}`}
                >
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{fmtDateTime(l.sent_at)}</td>
                  <td className="px-4 py-2.5 text-xs">{l.template_name || tplMap[l.template_id]?.name || l.template_id}{l.is_test && <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-800">TEST</span>}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{l.reservation_id.slice(0, 8)}...</td>
                  <td className="px-4 py-2.5 text-xs">{CHANNEL_LABEL[l.provider] || l.provider || "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{LANG_TABS.find((lt) => lt.code === l.language_used)?.flag || l.language_used}</td>
                  <td className="px-4 py-2.5 text-xs">
                    {l.status === "sent" ? (
                      isProxySent ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800" title="Resend je prihvatio, ali recipient je Booking.com proxy — klikni za detalje"><Warning size={10} weight="fill" /> poslano (proxy)</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800"><CheckCircle size={10} weight="fill" /> poslano</span>
                      )
                    ) : l.status === "failed" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-800">× greška</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700">{l.status === "dismissed" ? "ručno preskočeno" : "preskočeno"}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right text-[11px] text-muted-foreground">
                    <span data-testid={`log-detail-hint-${l.id}`}>pogledaj →</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {detailLog && (
        <DeliveryErrorModal log={detailLog} templateName={detailLog.template_name || tplMap[detailLog.template_id]?.name || detailLog.template_id} onClose={() => setDetailLog(null)} />
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// Delivery detail modal — adaptive for all statuses.
//   • sent (regular)       → emerald "Potvrda isporuke" (what/where/when)
//   • sent (proxy email)   → amber "Poslano, ali vjerojatno ne stiže gostu"
//   • skipped              → slate "Detalji preskakanja"
//   • failed               → rose  "Detalji greške"
// -----------------------------------------------------------------------------
function DeliveryErrorModal({ log, templateName, onClose }) {
  const [copied, setCopied] = useState(false);
  const errText = log.error || "(bez poruke)";
  const isProxySent = log.status === "sent"
    && log.provider === "email"
    && (log.recipient || "").toLowerCase().endsWith(".guest.booking.com");
  const isSuccess = log.status === "sent" && !isProxySent;
  const copyErr = async () => {
    try {
      await navigator.clipboard.writeText(errText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  };
  // Smart hints — matched against the error text.
  const hints = useMemo(() => {
    const e = errText.toLowerCase();
    const r = (log.recipient || "").toLowerCase();
    const tips = [];
    // Resend "sent" but recipient is a Booking.com proxy address — commonest
    // "poslano ali ne stiže" case. Shown even on status=sent via a dedicated
    // info row inside the modal (see context strip above).
    if (log.provider === "email" && (r.endsWith(".guest.booking.com") || r.endsWith("@guest.booking.com"))) {
      tips.push("Primatelj je Booking.com proxy adresa (`.guest.booking.com`) — Booking.com prosljeđuje mail gostu SAMO ako je tvoj `from` domain verificiran s SPF/DKIM na Resend-u. Za nesigurne domene (gmail, yahoo pošiljatelji ili neverificiran custom domain), proxy često tiho odbacuje mail iako Resend javlja 'sent'. Rješenje: 1) U Resend dashboard-u verificiraj svoj domain (`notifications@tvojadomena.hr`), 2) Postavi SPF+DKIM TXT zapise u Cloudflare, 3) Promijeni `resend_from_email` u Postavke → Auto poruke na verificirani from.");
    }
    if (e.includes("channex api key not set") || e.includes("channex client:") || e.includes("ota client:") || e.includes("api key nije postavljen") || e.includes("api ključ nije postavljen")) {
      tips.push("OTA sinkronizacija nema postavljen API ključ za ovaj račun. Ako koristiš Powered by Lodgy, kontaktiraj Lodgy podršku; ako koristiš vlastiti ključ, unesi ga u Postavke → Kanali (OTA) → Vlastiti OTA API ključ.");
    }
    if (e.includes("no channex thread") || e.includes("no ota thread") || e.includes("booking_id")) {
      tips.push("Rezervacija nema aktivni OTA chat. Poruka preko OTA Inboxa moguća je samo za Booking.com / Airbnb / Expedia rezervacije koje su stigle preko OTA sinkronizacije — ne za direktne rezervacije.");
    }
    if (e.includes("resend not configured")) {
      tips.push("Resend nije postavljen. Otvori Postavke → Auto poruke → unesi Resend API ključ i `from` email adresu (format: `Lodgy <notifications@tvojadomena.hr>`).");
    }
    if (e.includes("resend") && !e.includes("not configured")) {
      tips.push("Resend API je odgovorio greškom. Najčešći razlozi: 1) `from` adresa je NA domeni koja nije verificirana na Resend-u, 2) API ključ je za drugi Resend account, 3) tvoj Resend plan je bez credita.");
    }
    if (e.includes("twilio") || log.provider === "sms") {
      tips.push("SMS provider (Twilio) nije uspio poslati. Provjeri Twilio Account SID / Auth Token / From number u Postavkama → Providers. Broj gosta mora biti u E.164 formatu (+385...).");
    }
    if (e.includes("403") || e.includes("forbidden") || e.includes("messaging")) {
      tips.push("OTA poruke (Messaging) nisu aktivne za ovaj račun. Kontaktiraj Lodgy podršku da ih uključi.");
    }
    if (e.includes("401") || e.includes("unauthoriz")) {
      tips.push("OTA API ključ je nevažeći ili prazan. Otvori Postavke → Kanali (OTA) i provjeri ključ ili kontaktiraj Lodgy podršku.");
    }
    if (e.includes("no primary_guest_email") || e.includes("no guest email")) {
      tips.push("Rezervacija nema email gosta. Email template zahtijeva `primary_guest_email`. Dopuni ručno kroz Rezervacije → uredi gosta.");
    }
    if (e.includes("timeout") || e.includes("504")) {
      tips.push("Mrežni timeout prema vanjskom servisu. Pokušaj klikom 'Pokreni scheduler sada' za ponovni pokušaj.");
    }
    if (e.includes("not in template channels")) {
      tips.push("Rezervacija je s kanala kojeg template izričito ne pokriva (template ima filter kanala). Uredi template i uključi kanal rezervacije u 'Kanali' polje, ili ukloni filter.");
    }
    if (!tips.length) {
      tips.push("Nepoznata greška. Kopiraj puni tekst niže i pošalji na podršku.");
    }
    return tips;
  }, [errText, log.provider, log.recipient]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="delivery-error-modal"
    >
      <div
        className="w-full max-w-2xl rounded-lg border border-border bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
            {isSuccess ? (
              <CheckCircle size={18} className="text-emerald-600" weight="duotone" />
            ) : (
              <Warning size={18} className={isProxySent ? "text-amber-600" : "text-rose-600"} weight="duotone" />
            )}
            {isSuccess
              ? "Potvrda isporuke"
              : log.status === "failed"
                ? "Detalji greške"
                : log.status === "skipped"
                  ? "Detalji preskakanja"
                  : "Poslano, ali vjerojatno ne stiže gostu"}
          </h3>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted" data-testid="delivery-error-close">
            <XIcon size={16} />
          </button>
        </div>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto p-5 text-sm">
          {/* Context strip */}
          <div className="grid grid-cols-2 gap-2 rounded-md bg-muted/50 p-3 text-xs">
            <div><span className="font-semibold">Template:</span> {templateName}</div>
            <div><span className="font-semibold">Kanal:</span> {CHANNEL_LABEL[log.provider] || log.provider || "—"}</div>
            <div><span className="font-semibold">Jezik:</span> {LANG_TABS.find((l) => l.code === log.language_used)?.label || log.language_used}</div>
            <div><span className="font-semibold">Vrijeme:</span> {fmtDateTime(log.sent_at)}</div>
            <div className="col-span-2"><span className="font-semibold">Rezervacija:</span> <code className="rounded bg-background px-1.5 py-0.5 text-[11px]">{log.reservation_id}</code></div>
            {log.recipient && (
              <div className="col-span-2"><span className="font-semibold">Primatelj:</span> <code className="rounded bg-background px-1.5 py-0.5 text-[11px]">{log.recipient}</code></div>
            )}
            {log.subject && (
              <div className="col-span-2"><span className="font-semibold">Subject:</span> {log.subject}</div>
            )}
            {log.external_message_id && (
              <div className="col-span-2"><span className="font-semibold">ID na providera:</span> <code className="rounded bg-background px-1.5 py-0.5 text-[11px]">{log.external_message_id}</code></div>
            )}
          </div>

          {/* Error message — only for failed / proxy-sent */}
          {!isSuccess && (
            <div>
              <div className="mb-1 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {log.status === "skipped" ? "Razlog preskakanja" : isProxySent ? "Napomena o isporuci" : "Puna poruka greške"}
                </p>
                <button
                  onClick={copyErr}
                  className="flex items-center gap-1 rounded border border-border bg-background px-2 py-0.5 text-[11px] font-medium hover:bg-muted"
                  data-testid="delivery-error-copy"
                >
                  {copied ? <Check size={11} /> : <Copy size={11} />} {copied ? "Kopirano" : "Kopiraj"}
                </button>
              </div>
              <pre className={`whitespace-pre-wrap break-words rounded border p-3 text-[12px] ${
                isProxySent
                  ? "border-amber-200 bg-amber-50 text-amber-900"
                  : log.status === "skipped"
                    ? "border-slate-200 bg-slate-50 text-slate-900"
                    : "border-rose-200 bg-rose-50 text-rose-900"
              }`}>{isProxySent ? "Resend je prihvatio mail, ali recipient je Booking.com proxy adresa. Bez verificirane 'from' domene (SPF/DKIM), proxy često tiho odbaci poruku. Vidi savjete niže." : errText}</pre>
            </div>
          )}

          {/* Smart hints — only for failed / proxy-sent (nothing to fix if sent clean) */}
          {!isSuccess && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Što napraviti</p>
              <ul className="space-y-2">
                {hints.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2.5 text-[12px] text-amber-900">
                    <span className="shrink-0 font-bold">▸</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Rendered body — always shown (user wants to see WHAT was sent) */}
          {log.body_rendered && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {isSuccess ? "Sadržaj poslane poruke" : "Pokušani sadržaj poruke"}
              </p>
              <pre className={`max-h-80 overflow-auto whitespace-pre-wrap break-words rounded border p-3 text-[12px] ${
                isSuccess ? "border-emerald-200 bg-emerald-50 text-emerald-950" : "border-border bg-muted/50 text-foreground"
              }`}>{log.body_rendered}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Property / unit targeting for a template (empty = all).
function LocationPicker({ propertyIds, unitIds, onChange }) {
  const [props, setProps] = useState([]);
  const [units, setUnits] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/properties"), api.get("/units")])
      .then(([p, u]) => { setProps(p.data || []); setUnits((u.data || []).filter((x) => x.active !== false)); })
      .catch(() => {});
  }, []);
  const unitsOf = (pid) => units.filter((u) => u.property_id === pid);
  const stateOf = (pid) => {
    if (propertyIds.includes(pid)) return "all";
    return unitsOf(pid).some((u) => unitIds.includes(u.id)) ? "some" : "none";
  };
  const toggleProperty = (pid) => {
    const ids = unitsOf(pid).map((u) => u.id);
    if (stateOf(pid) === "all") onChange(propertyIds.filter((x) => x !== pid), unitIds.filter((x) => !ids.includes(x)));
    else onChange([...propertyIds, pid], unitIds.filter((x) => !ids.includes(x)));
  };
  const toggleUnit = (pid, uid) => {
    const ids = unitsOf(pid).map((u) => u.id);
    let selected = propertyIds.includes(pid) ? ids : ids.filter((x) => unitIds.includes(x));
    selected = selected.includes(uid) ? selected.filter((x) => x !== uid) : [...selected, uid];
    const others = unitIds.filter((x) => !ids.includes(x));
    const restProps = propertyIds.filter((x) => x !== pid);
    if (selected.length === ids.length && ids.length) onChange([...restProps, pid], others);
    else onChange(restProps, [...others, ...selected]);
  };
  const total = propertyIds.length + unitIds.length;
  const summary = total === 0 ? "Svi objekti" : [
    ...props.filter((p) => propertyIds.includes(p.id)).map((p) => pickLocale(p.name)),
    ...units.filter((u) => unitIds.includes(u.id)).map((u) => u.code),
  ].join(", ");
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className={`flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-md border px-3 text-sm ${total ? "border-primary bg-primary/5 text-foreground" : "border-border bg-background text-foreground"}`}
        data-testid="template-location-toggle">
        <span className="truncate">{summary}</span>
        <CaretDown size={12} className="shrink-0 opacity-60" />
      </button>
      {open && (
        <div className="mt-1 max-h-72 overflow-y-auto rounded-md border border-border bg-popover p-1.5 shadow-lg" data-testid="template-location-menu">
          <button type="button" onClick={() => onChange([], [])} className={`flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm hover:bg-muted ${total === 0 ? "font-semibold text-primary" : ""}`} data-testid="template-location-all">
            {total === 0 ? <CheckSquare size={16} weight="fill" className="shrink-0" /> : <Square size={16} className="shrink-0" />} Svi objekti
          </button>
          {props.map((p) => {
            const st = stateOf(p.id);
            return (
              <div key={p.id} className="border-t border-border/60 py-0.5">
                <button type="button" onClick={() => toggleProperty(p.id)} className="flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm font-medium hover:bg-muted" data-testid={`template-location-prop-${p.id}`}>
                  {st === "all" ? <CheckSquare size={16} weight="fill" className="shrink-0 text-primary" /> : st === "some" ? <MinusSquare size={16} weight="fill" className="shrink-0 text-primary" /> : <Square size={16} className="shrink-0" />}
                  <span className="truncate">{pickLocale(p.name)}</span>
                </button>
                {unitsOf(p.id).map((u) => {
                  const on = st === "all" || unitIds.includes(u.id);
                  return (
                    <button key={u.id} type="button" onClick={() => toggleUnit(p.id, u.id)} className="flex w-full items-center gap-2 rounded py-1.5 pl-8 pr-2 text-left text-xs text-muted-foreground hover:bg-muted" data-testid={`template-location-unit-${u.id}`}>
                      {on ? <CheckSquare size={14} weight="fill" className="shrink-0 text-primary" /> : <Square size={14} className="shrink-0" />}
                      <span className="truncate">{u.code}{u.name ? ` · ${pickLocale(u.name)}` : ""}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Editor modal
// -----------------------------------------------------------------------------
function EditorModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(initial);
  const [langTab, setLangTab] = useState("en");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState("");
  const [copiedVar, setCopiedVar] = useState("");
  const trig = TRIGGER_MAP[form.trigger_type] || {};

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateBody = (lang, val) => setForm((f) => ({ ...f, bodies: { ...f.bodies, [lang]: val } }));
  const toggleChannel = (c) => setForm((f) => ({ ...f, channels: f.channels.includes(c) ? f.channels.filter((x) => x !== c) : [...f.channels, c] }));
  const copyVar = async (v) => {
    const text = `{${v}}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for iframes / insecure contexts where the Clipboard API is blocked
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* ignore */ }
      ta.remove();
    }
    setCopiedVar(v);
    setTimeout(() => setCopiedVar(""), 1200);
  };

  const save = async () => {
    if (!form.name.trim()) { setTestMsg("Naziv je obavezan."); return; }
    if (!((form.bodies || {}).en || "").trim()) { setTestMsg("English tekst je obavezan — šalje se svim gostima čiji jezik nema prijevod."); setLangTab("en"); return; }
    setSaving(true);
    try {
      const payload = { ...form, email_subject: (form.email_subjects || {}).en || form.email_subject || "" };
      if (isEdit) {
        await api.put(`/message-templates/${initial.id}`, payload);
      } else {
        await api.post("/message-templates", payload);
      }
      onSaved();
    } catch (e) {
      setTestMsg(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const doPreview = async () => {
    setTestMsg("");
    try {
      const res = isEdit
        ? await api.post(`/message-templates/${initial.id}/preview`, { language: langTab })
        : { data: { language: langTab, rendered: renderLocalPreview((form.bodies || {})[langTab] || "", langTab) } };
      setPreview(res.data);
    } catch (e) {
      setTestMsg(formatApiError(e));
    }
  };

  const [testResId, setTestResId] = useState("");
  const [testEmailAddr, setTestEmailAddr] = useState("");
  const [testingEmail, setTestingEmail] = useState(false);
  const doTestSend = async () => {
    if (!isEdit) { setTestMsg("Prvo spremite template."); return; }
    if (!testResId.trim()) { setTestMsg("Upišite ID rezervacije."); return; }
    setTesting(true);
    setTestMsg("");
    try {
      const { data } = await api.post(`/message-templates/${initial.id}/test-send`, { reservation_id: testResId.trim(), language: langTab });
      // F8.5: test-send now returns { results: [...], summary: {...} }.
      // Build a per-channel summary line so user knows exactly what happened
      // on each channel (OTA sent / Email failed / SMS skipped, ...).
      const results = Array.isArray(data.results) ? data.results : (data.status ? [data] : []);
      if (!results.length) {
        setTestMsg("Nijedan kanal nije pokušan — provjeri postavke template-a.");
        return;
      }
      const CH = { email: "📧 Email", sms: "📱 SMS", channex: "💬 OTA", BookingCom: "💬 Booking.com", AirBNB: "💬 Airbnb", Expedia: "💬 Expedia" };
      const lines = results.map((r) => {
        const prov = CH[r.provider] || r.provider || "?";
        if (r.status === "sent") {
          const dest = r.recipient ? ` → ${r.recipient}` : "";
          return `✓ ${prov}: poslano [${r.language_used || "?"}]${dest}`;
        }
        if (r.status === "skipped") return `⊘ ${prov}: preskočeno — ${r.error || "bez razloga"}`;
        return `✗ ${prov}: ${r.error || "nepoznata greška"}`;
      });
      setTestMsg(lines.join("\n"));
    } catch (e) {
      setTestMsg(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  const doTestEmail = async () => {
    if (!isEdit) { setTestMsg("Prvo spremite template."); return; }
    const em = (testEmailAddr || "").trim();
    if (!em || !em.includes("@")) { setTestMsg("Upišite važeću email adresu."); return; }
    setTestingEmail(true);
    setTestMsg("");
    try {
      const { data } = await api.post(`/message-templates/${initial.id}/test-email`, {
        to_email: em,
        language: langTab,
      });
      setTestMsg(`✓ 📧 Email poslan [${data.language_used}] → ${data.recipient}\n  Subject: ${data.subject}\n  From: ${data.from}\n  Provjeri inbox (i spam folder prvi put!).`);
    } catch (e) {
      setTestMsg(formatApiError(e));
    } finally {
      setTestingEmail(false);
    }
  };

  return (
    <div className="rw-modal-overlay z-50" data-testid="template-editor-modal">
      <div className="rw-modal-panel-lg">
        {/* Header */}
        <div className="rw-modal-header">
          <h2 className="rw-h2">{isEdit ? "Uredi template" : "Novi template"}</h2>
          <button onClick={onClose} className="rw-btn-icon" data-testid="template-editor-close">
            <XIcon size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="rw-modal-body grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr),240px]">
          {/* Form column */}
          <div className="min-w-0 space-y-4">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Naziv</label>
              <input value={form.name} onChange={(e) => update("name", e.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none" placeholder="npr. Check-in link 3 dana prije" data-testid="template-name" />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Opis (interni)</label>
              <input value={form.description || ""} onChange={(e) => update("description", e.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none" data-testid="template-description" />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Kanali slanja (odaberi više)</label>
              <div className="flex flex-wrap gap-2" data-testid="template-delivery-channels">
                {DELIVERY_CHANNELS.map((dc) => {
                  const selected = (form.delivery_channels || []).includes(dc.key);
                  return (
                    <button
                      key={dc.key}
                      type="button"
                      onClick={() => {
                        setForm((f) => {
                          const current = f.delivery_channels || [];
                          const next = current.includes(dc.key)
                            ? current.filter((x) => x !== dc.key)
                            : [...current, dc.key];
                          // Keep legacy delivery_channel in sync for backward
                          // read compat (lists / older views).
                          return {
                            ...f,
                            delivery_channels: next,
                            delivery_channel: next[0] || "channex",
                          };
                        });
                      }}
                      className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition ${
                        selected ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-foreground hover:bg-muted"
                      }`}
                      data-testid={`template-delivery-${dc.key}`}
                    >
                      <span className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded border ${
                        selected ? "border-primary-foreground bg-primary-foreground/20" : "border-foreground/40 bg-card"
                      }`}>
                        {selected && <Check size={9} weight="bold" />}
                      </span>
                      {dc.label}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {(form.delivery_channels || []).length === 0
                  ? "Odaberi barem jedan kanal — inače se poruka neće poslati nigdje."
                  : (form.delivery_channels || []).length === 1
                    ? DELIVERY_CHANNELS.find((d) => d.key === form.delivery_channels[0])?.hint
                    : `Poruka će se poslati paralelno kroz ${form.delivery_channels.length} kanala — svaki uspjeh/greška se bilježi zasebno u Log slanja.`}
              </p>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Objekti i jedinice</label>
              <LocationPicker
                propertyIds={form.property_ids || []}
                unitIds={form.unit_ids || []}
                onChange={(pids, uids) => setForm((f) => ({ ...f, property_ids: pids, unit_ids: uids }))}
              />
              <p className="mt-1 truncate text-[11px] text-muted-foreground">Poruka se šalje samo gostima u odabranim objektima / jedinicama.</p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr,1fr,1fr]">
              <div className="min-w-0">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Trigger</label>
                <select value={form.trigger_type} onChange={(e) => update("trigger_type", e.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none" data-testid="template-trigger">
                  {TRIGGERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
                <p className="mt-1 text-[11px] text-muted-foreground">{trig.hint}</p>
              </div>
              {trig.needsOffset && (
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Broj dana</label>
                  <input type="number" min="0" max="90" value={form.trigger_offset_days} onChange={(e) => update("trigger_offset_days", parseInt(e.target.value || "0"))} className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none" data-testid="template-offset-days" />
                </div>
              )}
              {trig.needsTime && (
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Vrijeme slanja</label>
                  <input type="time" value={form.trigger_time} onChange={(e) => update("trigger_time", e.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none" data-testid="template-time" />
                </div>
              )}
            </div>

            {(form.delivery_channels || []).includes("channex") && (
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">OTA kanali (filter)</label>
                <div className="flex flex-wrap gap-2" data-testid="template-channels">
                  {CHANNELS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleChannel(c)}
                      className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                        form.channels.includes(c) ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-foreground hover:bg-muted"
                      }`}
                      data-testid={`template-channel-${c}`}
                    >
                      {CHANNEL_LABEL[c]}
                    </button>
                  ))}
                  <p className="ml-2 self-center text-[11px] text-muted-foreground">
                    {form.channels.length === 0 ? "Nijedan odabran = svi kanali" : `${form.channels.length} kanal(a)`}
                  </p>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">Filter vrijedi samo za slanje kroz OTA inbox. Email i SMS se šalju gostima sa svih kanala.</p>
              </div>
            )}

            {/* Language tabs + body */}
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tekst poruke po jeziku</label>
              <div className="grid grid-cols-4 gap-1 sm:flex" data-testid="template-lang-tabs">
                {LANG_TABS.map((lt) => {
                  const has = !!(form.bodies || {})[lt.code]?.trim();
                  return (
                    <button
                      key={lt.code}
                      type="button"
                      onClick={() => setLangTab(lt.code)}
                      className={`flex min-w-0 items-center justify-center gap-1 whitespace-nowrap rounded-t-md border-b-2 px-2 py-1.5 text-xs font-medium transition sm:px-3 ${
                        langTab === lt.code ? "border-primary bg-muted/40 text-foreground" : "border-transparent text-muted-foreground hover:bg-muted/30"
                      }`}
                      data-testid={`template-lang-${lt.code}`}
                    >
                      <span className="hidden sm:inline">{lt.flag}</span><span className="sm:hidden">{lt.code.toUpperCase()}</span><span className="hidden truncate sm:inline">{lt.label}</span>
                      {has && <CheckCircle size={10} weight="fill" className="text-emerald-500" />}
                    </button>
                  );
                })}
              </div>
              {(form.delivery_channels || []).includes("email") && (
                <input
                  value={(form.email_subjects || {})[langTab] || ""}
                  onChange={(e) => setForm((f) => ({ ...f, email_subjects: { ...(f.email_subjects || {}), [langTab]: e.target.value } }))}
                  className="w-full rounded-none border border-b-0 border-border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
                  placeholder={`Email naslov (${langTab.toUpperCase()}) — npr. ${langTab === "hr" ? "Vaš link za prijavu — {property_name}" : langTab === "de" ? "Ihr Check-in-Link — {property_name}" : langTab === "it" ? "Il tuo link per il check-in — {property_name}" : "Your check-in link — {property_name}"}`}
                  data-testid="template-email-subject"
                />
              )}
              <textarea
                rows={10}
                value={(form.bodies || {})[langTab] || ""}
                onChange={(e) => updateBody(langTab, e.target.value)}
                className="block w-full min-w-0 rounded-md rounded-tl-none border border-border bg-background px-3 py-2 font-mono text-sm focus:border-primary focus:outline-none"
                placeholder={langTab === "en" ? "Hi {guest_first_name}!\n\nYour arrival at {property_name} is in {nights} nights on {check_in_date}.\nPlease complete your check-in: {checkin_link}\n\nThank you!" : "Pozdrav {guest_first_name}!\n\nDolazak u {property_name}...\n\nLink za prijavu: {checkin_link}"}
                data-testid="template-body"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">Gost dobiva poruku na svom jeziku; bez prijevoda šalje se English.</p>
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={form.active} onChange={(e) => update("active", e.target.checked)} data-testid="template-active" />
                Aktivan
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={form.send_once_per_reservation} onChange={(e) => update("send_once_per_reservation", e.target.checked)} data-testid="template-once" />
                Šalji samo jednom po rezervaciji
              </label>
            </div>
          </div>

          {/* Variables sidebar */}
          <div className="rounded-md border border-border bg-muted/30 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Varijable</p>
            <div className="space-y-1" data-testid="template-var-list">
              {VARIABLES.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => copyVar(v)}
                  className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-[11px] font-mono hover:bg-background"
                  data-testid={`var-${v}`}
                >
                  <span>{`{${v}}`}</span>
                  {copiedVar === v ? <Check size={10} className="text-emerald-500" /> : <Copy size={10} className="text-muted-foreground" />}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Preview/test row */}
        {isEdit && (
          <div className="border-t border-border bg-muted/20 p-4">
            <div className="mb-2 grid grid-cols-[auto,minmax(0,1fr),auto] items-center gap-2">
              <button onClick={doPreview} className="inline-flex h-8 items-center gap-1 whitespace-nowrap rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium hover:bg-muted" data-testid="template-preview-btn">
                <Eye size={12} /> Preview
              </button>
              <input
                type="text"
                value={testResId}
                onChange={(e) => setTestResId(e.target.value)}
                placeholder="ID rezervacije"
                className="h-8 min-w-0 rounded-md border border-border bg-background px-2.5 py-1.5 font-mono text-xs focus:border-primary focus:outline-none"
                data-testid="template-test-resid"
              />
              <button onClick={doTestSend} disabled={testing || !testResId.trim()} className="inline-flex h-8 items-center gap-1 rounded-md bg-amber-500 px-2.5 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-40" data-testid="template-testsend-btn">
                {testing ? <CircleNotch size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} weight="fill" />} <span className="whitespace-nowrap">Test</span>
              </button>
            </div>
            {/* F8.6: standalone test email — pošalji na proizvoljnu adresu bez
                rezervacije. Radi SAMO ako je Email jedan od delivery_channels
                i Resend je konfiguriran. */}
            <div className="mb-2 grid grid-cols-[auto,minmax(0,1fr),auto] items-center gap-2">
              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-dashed border-border bg-background px-2.5 py-1.5 text-[11px] text-muted-foreground">
                <EnvelopeSimple size={12} /> Email
              </span>
              <input
                type="email"
                value={testEmailAddr}
                onChange={(e) => setTestEmailAddr(e.target.value)}
                placeholder="tvoj.email@gmail.com"
                className="h-8 min-w-0 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs focus:border-primary focus:outline-none"
                data-testid="template-test-email-input"
              />
              <button
                onClick={doTestEmail}
                disabled={testingEmail || !testEmailAddr.trim()}
                className="inline-flex h-8 items-center gap-1 rounded-md bg-sky-500 px-2.5 text-xs font-semibold text-white hover:bg-sky-600 disabled:opacity-40"
                data-testid="template-test-email-btn"
                title="Pošalji ovaj template kao test email na upisanu adresu (bez rezervacije — koristi STUB podatke za placeholder-e)"
              >
                {testingEmail ? <CircleNotch size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} weight="fill" />} <span className="whitespace-nowrap">Pošalji</span>
              </button>
            </div>
            {preview && (
              <div className="rounded-md border border-primary/40 bg-background p-3" data-testid="template-preview-box">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Preview ({preview.language})</p>
                <pre className="whitespace-pre-wrap break-words font-sans text-sm text-foreground">{preview.rendered}</pre>
              </div>
            )}
            {testMsg && <pre className="mt-2 whitespace-pre-wrap break-words font-sans text-xs text-foreground" data-testid="template-test-msg">{testMsg}</pre>}
          </div>
        )}

        {/* Footer */}
        <div className="rw-modal-footer">
          <button onClick={onClose} className="rw-btn-secondary rw-btn-sm">Odustani</button>
          <button onClick={save} disabled={saving} className="rw-btn-primary rw-btn-sm" data-testid="template-save-btn">
            {saving ? <CircleNotch size={14} className="animate-spin" /> : <Check size={14} />} {isEdit ? "Spremi" : "Kreiraj"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Simple local renderer for the "new template" preview (no server call available yet)
function renderLocalPreview(body, lang) {
  const sample = {
    guest_first_name: "John", guest_last_name: "Doe", guest_full_name: "John Doe",
    reservation_code: "RW-PREVIEW", check_in_date: "2026-08-15", check_out_date: "2026-08-18",
    nights: "3", adults: "2", children: "0", property_name: "Sample Apartments",
    property_address: "Ilica 1, Zagreb", unit_name: "Apt 1", unit_code: "A1",
    checkin_link: "https://.../check-in/r/SAMPLE-TOKEN", tenant_name: "Lodgy",
    check_in_time: "15:00", check_out_time: "10:00",
    unit_instructions_link: "https://.../upute-apartman-a.pdf",
    unit_instructions_link_en: "https://.../upute-a-en.pdf", unit_instructions_link_hr: "https://.../upute-a-hr.pdf",
    unit_instructions_link_de: "https://.../upute-a-de.pdf", unit_instructions_link_it: "https://.../upute-a-it.pdf",
  };
  return body.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (_, k) => sample[k] ?? `{${k}}`);
}

// -----------------------------------------------------------------------------
// Providers settings — Resend (email) + Twilio (SMS) credentials
// -----------------------------------------------------------------------------
function ProvidersSettings() {
  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState({
    resend_api_key: "",
    resend_from_email: "",
    twilio_account_sid: "",
    twilio_auth_token: "",
    twilio_phone_number: "",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/tenants/me").then((r) => {
      setTenant(r.data);
      setForm((f) => ({ ...f, resend_from_email: r.data.resend_from_email || "", twilio_phone_number: r.data.twilio_phone_number || "" }));
    });
  }, []);

  const emailConfigured = !!(tenant?.resend_api_key && tenant?.resend_from_email);
  const smsConfigured = !!(tenant?.twilio_account_sid && tenant?.twilio_auth_token && tenant?.twilio_phone_number);

  const submit = async () => {
    setSaving(true); setMsg("");
    try {
      const payload = {};
      // Only send fields the user filled in — empty string means "do not change masked value"
      if (form.resend_api_key.trim()) payload.resend_api_key = form.resend_api_key.trim();
      if (form.resend_from_email.trim() || tenant?.resend_from_email) payload.resend_from_email = form.resend_from_email.trim();
      if (form.twilio_account_sid.trim()) payload.twilio_account_sid = form.twilio_account_sid.trim();
      if (form.twilio_auth_token.trim()) payload.twilio_auth_token = form.twilio_auth_token.trim();
      if (form.twilio_phone_number.trim() || tenant?.twilio_phone_number) payload.twilio_phone_number = form.twilio_phone_number.trim();
      await api.put("/tenants/me", payload);
      const { data } = await api.get("/tenants/me");
      setTenant(data);
      setForm({ resend_api_key: "", resend_from_email: data.resend_from_email || "", twilio_account_sid: "", twilio_auth_token: "", twilio_phone_number: data.twilio_phone_number || "" });
      setMsg("✓ Postavke spremljene.");
    } catch (e) {
      setMsg(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!tenant) return <div className="flex h-32 items-center justify-center"><CircleNotch size={20} className="animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4" data-testid="providers-settings">
      {/* RESEND */}
      <section className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2">
          <Envelope size={18} weight="duotone" className="text-primary" />
          <h3 className="text-base font-semibold text-foreground">Resend (Email)</h3>
          {emailConfigured && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">Konfigurirano</span>}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Transakcijski email provider. Kreiraj API ključ na{" "}
          <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-primary underline">resend.com/api-keys</a>{" "}
          i verificiraj domenu na{" "}
          <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-primary underline">resend.com/domains</a>.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">API Key</label>
            <input
              type="password"
              value={form.resend_api_key}
              onChange={(e) => setForm((f) => ({ ...f, resend_api_key: e.target.value }))}
              className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
              placeholder={tenant.resend_api_key || "re_..."}
              data-testid="resend-api-key"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">{tenant.resend_api_key ? "Trenutno: " + tenant.resend_api_key + " (ostavi prazno da zadržiš)" : "Nije postavljen"}</p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">From email</label>
            <input
              value={form.resend_from_email}
              onChange={(e) => setForm((f) => ({ ...f, resend_from_email: e.target.value }))}
              className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
              placeholder="Apartments Rijeka <no-reply@yourdomain.com>"
              data-testid="resend-from-email"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">Mora koristiti verificiranu domenu.</p>
          </div>
        </div>
      </section>

      {/* TWILIO */}
      <section className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2">
          <DeviceMobile size={18} weight="duotone" className="text-primary" />
          <h3 className="text-base font-semibold text-foreground">Twilio (SMS)</h3>
          {smsConfigured && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">Konfigurirano</span>}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          SMS provider. Account SID i Auth Token nalaziš u{" "}
          <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="text-primary underline">Twilio Console</a>.
          Broj mora biti SMS-capable Twilio broj u E.164 formatu (npr. +385911234567).
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Account SID</label>
            <input
              type="password"
              value={form.twilio_account_sid}
              onChange={(e) => setForm((f) => ({ ...f, twilio_account_sid: e.target.value }))}
              className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
              placeholder={tenant.twilio_account_sid || "AC..."}
              data-testid="twilio-sid"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">{tenant.twilio_account_sid ? "Trenutno: " + tenant.twilio_account_sid : "Nije postavljen"}</p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Auth Token</label>
            <input
              type="password"
              value={form.twilio_auth_token}
              onChange={(e) => setForm((f) => ({ ...f, twilio_auth_token: e.target.value }))}
              className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
              placeholder={tenant.twilio_auth_token || "..."}
              data-testid="twilio-token"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">{tenant.twilio_auth_token ? "Trenutno: " + tenant.twilio_auth_token : "Nije postavljen"}</p>
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Twilio broj (From)</label>
            <input
              value={form.twilio_phone_number}
              onChange={(e) => setForm((f) => ({ ...f, twilio_phone_number: e.target.value }))}
              className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm font-mono focus:border-primary focus:outline-none"
              placeholder="+385911234567"
              data-testid="twilio-phone"
            />
          </div>
        </div>
      </section>

      <div className="sticky bottom-4 z-10 flex items-center justify-between rounded-lg border border-border bg-card p-3 shadow-lg">
        <span className="text-xs text-muted-foreground">{msg}</span>
        <button
          onClick={submit}
          disabled={saving}
          className="rw-btn-primary rw-btn-sm"
          data-testid="providers-save"
        >
          <FloppyDisk size={14} weight="duotone" />
          {saving ? "Spremam..." : "Spremi postavke"}
        </button>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// Diagnostic "Run scheduler now" — forces an immediate tick on the backend.
// Lets operators verify auto-send without waiting for the 5-minute loop or
// the 10:00 trigger_time window. Only called manually from the Auto poruke
// page header, so no idempotency concern (scheduler itself still enforces
// send_once_per_reservation).
// -----------------------------------------------------------------------------
const TRIGGER_LBL = {
  on_booking_confirmed: "nova rezervacija", n_days_before_checkin: "prije dolaska", on_checkin_day: "na dan dolaska",
  on_checkin_form_submitted: "check-in obrazac", n_days_after_checkout: "nakon odlaska",
};
const pickKey = (t, r) => `${t}|${r}`;

function RunSchedulerNowButton({ onDone }) {
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const close = () => { setPreview(null); setResult(null); setErr(""); };

  const loadPreview = async () => {
    setRunning(true); setErr(""); setResult(null);
    try {
      const { data } = await api.post("/message-templates/run-scheduler-now", null, { params: { dry_run: true } });
      const sel = {};
      (data.report || []).forEach((row) => (row.results || []).forEach((r) => { sel[pickKey(row.template_id, r.reservation_id)] = false; }));
      setSelected(sel);
      setPreview(data);
    } catch (e) { setErr(formatApiError(e)); } finally { setRunning(false); }
  };

  const toPairs = (val) => Object.entries(selected).filter(([, v]) => v === val)
    .map(([k]) => { const [template_id, reservation_id] = k.split("|"); return { template_id, reservation_id }; });

  const confirm = async (dismissRest) => {
    const send = toPairs(true);
    const dismiss = dismissRest ? toPairs(false) : [];
    if (!send.length && !dismiss.length) return;
    setRunning(true); setErr("");
    try {
      const { data } = await api.post("/message-templates/run-scheduler-now", { send, dismiss }, { params: { dry_run: false } });
      setResult(data); setPreview(null);
      onDone?.();
      if (data.sent_count) toast.success(`Poslano: ${data.sent_count}`);
    } catch (e) { setErr(formatApiError(e)); } finally { setRunning(false); }
  };

  const total = Object.keys(selected).length;
  const checked = Object.values(selected).filter(Boolean).length;
  const setAll = (v) => setSelected((s) => Object.fromEntries(Object.keys(s).map((k) => [k, v])));

  return (
    <>
      <button
        type="button"
        onClick={loadPreview}
        disabled={running}
        className="inline-flex h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-border bg-card px-3 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
        title="Pregledaj poruke na čekanju i odaberi koje poslati"
        data-testid="run-scheduler-now-btn"
      >
        <Play size={16} />
        <span className="hidden sm:inline">{running && !preview ? "Učitavam…" : "Pokreni sada"}</span>
      </button>

      {(preview || result || err) && (
        <div onClick={(e) => e.target === e.currentTarget && close()} className="fixed inset-0 z-[200] flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
          <div className="flex max-h-[90dvh] w-full max-w-2xl flex-col overflow-hidden rounded-t-xl border border-border bg-background shadow-xl sm:rounded-lg" data-testid="scheduler-preview-modal">
            <div className="flex items-center justify-between border-b border-border px-5 py-3">
              <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
                <Play size={16} weight="duotone" className="text-primary" />
                {result ? "Rezultat slanja" : "Pregled poruka na čekanju"}
              </h3>
              <button onClick={close} className="rounded-md p-1 hover:bg-muted" data-testid="scheduler-preview-close"><XIcon size={16} /></button>
            </div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
              {err && (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive" data-testid="scheduler-error">
                  <Warning size={14} weight="duotone" className="inline" /> {err}
                </div>
              )}
              {preview && <SchedulerPreviewList preview={preview} selected={selected} setSelected={setSelected} total={total} checked={checked} setAll={setAll} />}
              {result && <SchedulerResultList result={result} />}
            </div>
            {preview && total > 0 && (
              <div className="flex flex-col gap-2 border-t border-border px-5 py-3 sm:flex-row sm:items-center sm:justify-end">
                <button onClick={() => { const n = total - checked; if (n === 0 || window.confirm(`Trajno preskočiti ${n} poruk${n === 1 ? "u" : "a"}? Te poruke se više nikad neće poslati.`)) confirm(true); }} disabled={running} className="inline-flex h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-border px-3 text-xs font-semibold hover:bg-muted disabled:opacity-50" data-testid="scheduler-send-dismiss-rest" title="Odznačene poruke se nikad neće automatski poslati">
                  {checked ? "Pošalji odabrane, " : ""}trajno preskoči {total - checked}
                </button>
                <button onClick={() => confirm(false)} disabled={running || checked === 0} className="inline-flex h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary px-3 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50" data-testid="scheduler-send-selected">
                  {running ? <CircleNotch size={12} className="animate-spin" /> : <Play size={12} weight="fill" />} Pošalji odabrane ({checked})
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function SchedulerPreviewList({ preview, selected, setSelected, total, checked, setAll }) {
  if (total === 0) {
    return <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="scheduler-preview-empty">Nema poruka na čekanju — ništa se ne bi poslalo.</p>;
  }
  return (
    <>
      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-foreground">
        Ništa još nije poslano. Označi goste kojima želiš poslati poruku. Odznačeni gosti koje „trajno preskočiš” neće dobiti tu poruku ni automatski.
      </div>
      <div className="flex items-center gap-3 text-xs">
        <span><b>{checked}</b> / {total} odabrano</span>
        <button onClick={() => setAll(true)} className="ml-auto font-medium text-primary hover:underline" data-testid="scheduler-select-all">Označi sve</button>
        <button onClick={() => setAll(false)} className="font-medium text-muted-foreground hover:underline" data-testid="scheduler-select-none">Odznači sve</button>
      </div>
      {preview.report.filter((row) => row.results.length).map((row) => (
        <div key={row.template_id} className="rounded-md border border-border" data-testid={`scheduler-tpl-${row.template_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/30 px-3 py-2">
            <p className="text-sm font-semibold text-foreground">{row.name}</p>
            <span className="text-[11px] text-muted-foreground">{TRIGGER_LBL[row.trigger_type] || row.trigger_type} · {row.trigger_time} · {row.delivery_channel}</span>
          </div>
          <ul className="divide-y divide-border">
            {row.results.map((r) => {
              const k = pickKey(row.template_id, r.reservation_id);
              return (
                <li key={k}>
                  <label className="flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-muted/40">
                    <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]" checked={!!selected[k]} onChange={(e) => setSelected((s) => ({ ...s, [k]: e.target.checked }))} data-testid={`scheduler-pick-${row.template_id}-${r.reservation_id}`} />
                    <div className="min-w-0 flex-1 text-xs">
                      <p className="truncate font-medium text-foreground">{r.guest || "Gost bez imena"} {r.code && <span className="text-muted-foreground">· {r.code}</span>}</p>
                      <p className="text-muted-foreground">{r.check_in} → {r.check_out}{r.source ? ` · ${r.source}` : ""}</p>
                      {r.previously_failed && <p className="mt-0.5 text-[11px] text-destructive">Ranije neuspjelo{r.error ? `: ${r.error}` : ""}</p>}
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </>
  );
}

function SchedulerResultList({ result }) {
  const TONE = { sent: "border-emerald-500/40 bg-emerald-500/5 text-emerald-700", dismissed: "border-slate-300 bg-slate-50 text-slate-700", skipped: "border-amber-500/40 bg-amber-500/5 text-amber-700" };
  const LBL = { sent: "poslano", dismissed: "trajno preskočeno", skipped: "preskočeno", failed: "greška" };
  if (!result.results.length) return <p className="text-sm text-muted-foreground">Ništa nije obrađeno.</p>;
  return (
    <ul className="space-y-1 text-xs" data-testid="scheduler-result-list">
      {result.results.map((r, i) => (
        <li key={i} className={`rounded-md border px-2 py-1.5 ${TONE[r.status] || "border-destructive/40 bg-destructive/5 text-destructive"}`}>
          <b>{LBL[r.status] || r.status}</b> · {r.name} · {r.guest || r.reservation_id}
          {r.error && <span className="block text-[11px] opacity-80">{r.error}</span>}
        </li>
      ))}
    </ul>
  );
}
